import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCapIcon, CarIcon, DumbbellIcon, BriefcaseIcon, StarIcon, BookOpenIcon } from 'lucide-react';
interface Player {
  id: string;
  name: string;
  color: string;
  pathIndex: number | null;
  avatar?: string | null;
}
interface JourneyStartSceneProps {
  players: Player[];
  onStartPlaying: () => void;
}
const PATH_COLORS = [{
  stroke: '#8b5cf6',
  fill: '#a78bfa',
  name: 'School',
  emoji: '🎓',
  tileColor: '#c084fc'
}, {
  stroke: '#ec4899',
  fill: '#f472b6',
  name: 'Workforce',
  emoji: '💼',
  tileColor: '#f9a8d4'
}, {
  stroke: '#f59e0b',
  fill: '#fbbf24',
  name: 'Trade',
  emoji: '🔧',
  tileColor: '#fcd34d'
}, {
  stroke: '#14b8a6',
  fill: '#2dd4bf',
  name: 'Self-Made',
  emoji: '✨',
  tileColor: '#5eead4'
}];
// Sample tiles for the foreground path
const SAMPLE_TILES = [
// Row 1 (closest)
{
  icon: GraduationCapIcon,
  text: 'Study Hard\nPay Off',
  amount: '+$150',
  lane: 0
}, {
  icon: BookOpenIcon,
  text: 'Kid Needed\nSchool Supplies',
  amount: '-$80',
  lane: 1
}, {
  icon: CarIcon,
  text: 'Car Broke\nDown',
  amount: '-$200',
  lane: 2
}, {
  icon: DumbbellIcon,
  text: 'Invest in\nYourself',
  amount: '+$250',
  lane: 3
},
// Row 2
{
  icon: GraduationCapIcon,
  text: 'Got a\nPart-Time Job',
  amount: '+$120',
  lane: 1
}, {
  icon: StarIcon,
  text: 'Side Hustle\nSuccess',
  amount: '+$300',
  lane: 2
}];
export function JourneyStartScene({
  players,
  onStartPlaying
}: JourneyStartSceneProps) {
  // Group players by their chosen path
  const playersByPath: Record<number, Player[]> = {
    0: [],
    1: [],
    2: [],
    3: []
  };
  players.forEach(p => {
    const idx = p.pathIndex !== null && p.pathIndex !== undefined ? Math.max(0, Math.min(3, p.pathIndex)) : 0;
    playersByPath[idx].push(p);
  });
  return <div className="fixed inset-0 w-full h-full z-30 overflow-hidden" style={{
    background: 'linear-gradient(to bottom, #87CEEB 0%, #B0D4E3 100%)'
  }} data-id="element-194">
      {/* Clouds */}
      <div className="absolute top-8 left-[10%] w-32 h-16 bg-white/70 rounded-full blur-md" data-id="element-195" />
      <div className="absolute top-12 right-[15%] w-40 h-20 bg-white/60 rounded-full blur-md" data-id="element-196" />
      <div className="absolute top-20 left-[30%] w-36 h-18 bg-white/50 rounded-full blur-md" data-id="element-197" />

      {/* Left cityscape buildings */}
      <div className="absolute left-0 top-[15%] bottom-0 w-[25%] flex flex-col gap-2 pointer-events-none" data-id="element-198">
        {/* Main billboard building */}
        <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-tr-lg h-[60%] shadow-2xl" data-id="element-199">
          {/* Big billboard */}
          <div className="absolute top-8 left-4 right-4 bg-gradient-to-br from-purple-600 via-pink-500 to-purple-700 rounded-xl p-6 shadow-2xl border-4 border-white/20" style={{
          height: '70%'
        }} data-id="element-200">
            <div className="relative h-full flex flex-col" data-id="element-201">
              <div className="flex-1 flex flex-col justify-center" data-id="element-202">
                <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-2" style={{
                fontFamily: '"Dancing Script", cursive'
              }} data-id="element-203">
                  Trippin'
                  <br data-id="element-204" />
                  Through Life
                </h2>
                <p className="text-2xl md:text-3xl text-pink-200 font-bold" style={{
                fontFamily: '"Dancing Script", cursive'
              }} data-id="element-205">
                  with Mandy
                </p>
              </div>
              <div className="mt-4" data-id="element-206">
                <div className="bg-pink-500 text-white font-bold py-2 px-4 rounded-full text-center text-sm mb-2" data-id="element-207">
                  LIVE YOUR STORY
                </div>
                <p className="text-white text-xs font-bold text-center" data-id="element-208">
                  🎲 ROLL THE DICE. MAKE CHOICES. BUILD YOUR LIFE. 🎲
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary buildings */}
        <div className="flex gap-2 h-[35%]" data-id="element-209">
          <div className="flex-1 bg-gradient-to-b from-gray-700 to-gray-800 rounded-tr-lg relative" data-id="element-210">
            <div className="absolute top-4 left-2 right-2 bg-pink-400 text-white text-xs font-bold p-2 rounded text-center" data-id="element-211">
              GOOD
              <br data-id="element-212" />
              VIBES
              <br data-id="element-213" />
              ONLY
              <br data-id="element-214" />
              🌸
            </div>
          </div>
          <div className="flex-1 bg-gradient-to-b from-gray-600 to-gray-700 rounded-tr-lg relative" data-id="element-215">
            <div className="absolute top-4 left-2 right-2 bg-purple-500 text-white text-xs font-bold p-2 rounded text-center" data-id="element-216">
              LIVE
              <br data-id="element-217" />
              YOUR
              <br data-id="element-218" />
              DREAMS
            </div>
          </div>
        </div>
      </div>

      {/* Center tower billboard */}
      <div className="absolute left-1/2 -translate-x-1/2 top-[8%] w-24 md:w-32 h-[50%] bg-gradient-to-b from-gray-800 to-gray-900 rounded-t-lg shadow-2xl flex flex-col overflow-hidden" data-id="element-219">
        <div className="bg-pink-500 text-white font-bold text-center py-3 text-xs md:text-sm" data-id="element-220">
          TRIPPIN'
          <br data-id="element-221" />
          THROUGH LIFE
        </div>
        <div className="bg-teal-400 text-white font-bold text-center py-3 text-xs md:text-sm flex items-center justify-center gap-1" data-id="element-222">
          LIVE YOUR
          <br data-id="element-223" />
          LIFE 🎲
        </div>
        <div className="bg-purple-500 text-white font-bold text-center py-3 text-xs md:text-sm flex items-center justify-center gap-1" data-id="element-224">
          MAKE
          <br data-id="element-225" />
          CHOICES ❤️
        </div>
        <div className="bg-orange-500 text-white font-bold text-center py-3 text-xs md:text-sm flex items-center justify-center gap-1" data-id="element-226">
          BUILD YOUR
          <br data-id="element-227" />
          FUTURE ⭐
        </div>
      </div>

      {/* Right cityscape buildings */}
      <div className="absolute right-0 top-[15%] bottom-0 w-[25%] flex flex-col gap-2 pointer-events-none" data-id="element-228">
        <div className="relative bg-gradient-to-b from-gray-700 to-gray-800 rounded-tl-lg h-[50%] shadow-2xl" data-id="element-229">
          <div className="absolute top-8 left-4 right-4 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl p-4 shadow-xl" data-id="element-230">
            <p className="text-white font-bold text-xl md:text-2xl text-center leading-tight" data-id="element-231">
              LEVEL UP
              <br data-id="element-232" />
              EVERY DAY
              <br data-id="element-233" />
              <span className="text-4xl" data-id="element-234">↗</span>
            </p>
          </div>
        </div>
        <div className="relative bg-gradient-to-b from-gray-600 to-gray-700 rounded-tl-lg h-[30%] shadow-2xl" data-id="element-235">
          <div className="absolute top-4 left-4 right-4 bg-purple-600 text-white font-bold text-center py-3 px-2 rounded text-sm" data-id="element-236">
            MANIFEST
            <br data-id="element-237" />
            YOUR
            <br data-id="element-238" />
            FUTURE
          </div>
        </div>
        <div className="relative bg-gradient-to-b from-gray-700 to-gray-800 rounded-tl-lg h-[15%] shadow-xl" data-id="element-239">
          <div className="absolute top-2 left-2 right-2 bg-pink-500 text-white font-bold text-center py-2 rounded text-xs" data-id="element-240">
            GOOD THINGS
            <br data-id="element-241" />
            TAKE TIME
          </div>
        </div>
      </div>

      {/* Additional side buildings for depth */}
      <div className="absolute left-[25%] top-[25%] bottom-0 w-[10%] bg-gradient-to-b from-gray-600 to-gray-700 rounded-t-lg shadow-xl" data-id="element-242" />
      <div className="absolute right-[25%] top-[25%] bottom-0 w-[10%] bg-gradient-to-b from-gray-600 to-gray-700 rounded-t-lg shadow-xl" data-id="element-243" />

      {/* Cherry blossom branches - top left */}
      <svg className="absolute top-0 left-0 w-64 h-64 pointer-events-none" viewBox="0 0 200 200" data-id="element-244">
        <path d="M 0 0 Q 50 30 100 50 T 150 80" stroke="#8B4513" strokeWidth="4" fill="none" data-id="element-245" />
        {[...Array(15)].map((_, i) => <motion.circle key={i} cx={20 + i * 10 + Math.random() * 20} cy={10 + i * 5 + Math.random() * 20} r={3 + Math.random() * 2} fill={i % 2 === 0 ? '#FFB6C1' : '#FFC0CB'} initial={{
        opacity: 0.7
      }} animate={{
        opacity: [0.7, 1, 0.7]
      }} transition={{
        duration: 2 + Math.random(),
        repeat: Infinity,
        delay: Math.random()
      }} data-id="element-246" />)}
      </svg>

      {/* Cherry blossom branches - top right */}
      <svg className="absolute top-0 right-0 w-64 h-64 pointer-events-none" viewBox="0 0 200 200" data-id="element-247">
        <path d="M 200 0 Q 150 30 100 50 T 50 80" stroke="#8B4513" strokeWidth="4" fill="none" data-id="element-248" />
        {[...Array(15)].map((_, i) => <motion.circle key={i} cx={180 - i * 10 - Math.random() * 20} cy={10 + i * 5 + Math.random() * 20} r={3 + Math.random() * 2} fill={i % 2 === 0 ? '#FFB6C1' : '#FFC0CB'} initial={{
        opacity: 0.7
      }} animate={{
        opacity: [0.7, 1, 0.7]
      }} transition={{
        duration: 2 + Math.random(),
        repeat: Infinity,
        delay: Math.random()
      }} data-id="element-249" />)}
      </svg>

      {/* 3D Perspective Path Container */}
      <div className="absolute inset-0" style={{
      transformStyle: 'preserve-3d',
      perspective: '800px'
    }} data-id="element-250">
        <div className="absolute inset-0" style={{
        transformStyle: 'preserve-3d',
        transform: 'rotateX(60deg)',
        transformOrigin: 'center 75%'
      }} data-id="element-251">
          {/* The winding multi-lane path */}
          <div className="absolute left-1/2 -translate-x-1/2 bottom-[5%] w-[90%] max-w-5xl" data-id="element-252">
            {/* Path tiles - 2 rows visible */}
            <div className="relative" data-id="element-253">
              {/* Row 1 - Foreground (largest) */}
              <div className="flex gap-2 mb-2" data-id="element-254">
                {[0, 1, 2, 3].map(lane => {
                const tile = SAMPLE_TILES.find(t => t.lane === lane && SAMPLE_TILES.indexOf(t) < 4);
                if (!tile) return null;
                const Icon = tile.icon;
                return <div key={lane} className="flex-1 rounded-2xl shadow-2xl p-4 flex flex-col items-center justify-center relative overflow-hidden" style={{
                  background: PATH_COLORS[lane].tileColor,
                  minHeight: '160px',
                  border: `4px solid ${PATH_COLORS[lane].stroke}`
                }} data-id="element-255">
                      {/* Glossy overlay */}
                      <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent pointer-events-none" data-id="element-256" />

                      <Icon className="w-10 h-10 text-gray-800 mb-2" data-id="element-257" />
                      <p className="text-center text-sm font-bold text-gray-900 leading-tight whitespace-pre-line" data-id="element-258">
                        {tile.text}
                      </p>
                      <p className={`text-xl font-bold mt-2 ${tile.amount.startsWith('+') ? 'text-green-700' : 'text-red-700'}`} data-id="element-259">
                        {tile.amount}
                      </p>

                      {/* Dashed lane line on right edge */}
                      {lane < 3 && <div className="absolute right-0 top-0 bottom-0 w-1 flex flex-col justify-around" data-id="element-260">
                          {[...Array(6)].map((_, i) => <div key={i} className="w-1 h-3 bg-white/60 rounded" data-id="element-261" />)}
                        </div>}
                    </div>;
              })}
              </div>

              {/* Row 2 - Mid-distance (smaller) */}
              <div className="flex gap-1 mb-1 opacity-90" data-id="element-262">
                {[1, 2].map(lane => {
                const tile = SAMPLE_TILES.find(t => t.lane === lane && SAMPLE_TILES.indexOf(t) >= 4);
                if (!tile) return null;
                const Icon = tile.icon;
                return <div key={lane} className="flex-1 rounded-xl shadow-xl p-3 flex flex-col items-center justify-center relative" style={{
                  background: PATH_COLORS[lane].tileColor,
                  minHeight: '100px',
                  border: `3px solid ${PATH_COLORS[lane].stroke}`
                }} data-id="element-263">
                      <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent pointer-events-none rounded-xl" data-id="element-264" />
                      <Icon className="w-6 h-6 text-gray-800 mb-1" data-id="element-265" />
                      <p className="text-center text-xs font-bold text-gray-900 leading-tight whitespace-pre-line" data-id="element-266">
                        {tile.text}
                      </p>
                      <p className={`text-sm font-bold mt-1 ${tile.amount.startsWith('+') ? 'text-green-700' : 'text-red-700'}`} data-id="element-267">
                        {tile.amount}
                      </p>
                    </div>;
              })}
              </div>

              {/* Row 3 - Far distance (tiny, fading) */}
              <div className="flex gap-1 opacity-60" data-id="element-268">
                {[0, 1, 2, 3].map(lane => <div key={lane} className="flex-1 rounded-lg shadow-lg" style={{
                background: PATH_COLORS[lane].tileColor,
                minHeight: '40px',
                border: `2px solid ${PATH_COLORS[lane].stroke}`
              }} data-id="element-269" />)}
              </div>
            </div>

            {/* 3D Dice - left foreground */}
            <motion.div className="absolute -left-16 -bottom-8 w-24 h-24" animate={{
            rotateY: [0, 360]
          }} transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear'
          }} style={{
            transformStyle: 'preserve-3d'
          }} data-id="element-270">
              <div className="w-full h-full bg-white rounded-xl shadow-2xl relative" style={{
              transform: 'rotateX(-55deg)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)'
            }} data-id="element-271">
                {/* Dice dots */}
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-272">
                  <div className="grid grid-cols-3 gap-2 p-4" data-id="element-273">
                    <div className="w-3 h-3 bg-gray-900 rounded-full" data-id="element-274" />
                    <div data-id="element-275" />
                    <div className="w-3 h-3 bg-gray-900 rounded-full" data-id="element-276" />
                    <div data-id="element-277" />
                    <div className="w-3 h-3 bg-gray-900 rounded-full" data-id="element-278" />
                    <div data-id="element-279" />
                    <div className="w-3 h-3 bg-gray-900 rounded-full" data-id="element-280" />
                    <div data-id="element-281" />
                    <div className="w-3 h-3 bg-gray-900 rounded-full" data-id="element-282" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* 3D Purple Pawn - right foreground */}
            <motion.div className="absolute -right-12 -bottom-12" animate={{
            y: [0, -8, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} style={{
            transform: 'rotateX(-55deg)'
          }} data-id="element-283">
              <svg width="80" height="120" viewBox="0 0 80 120" data-id="element-284">
                <defs data-id="element-285">
                  <linearGradient id="pawnGrad" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-286">
                    <stop offset="0%" stopColor="#a78bfa" data-id="element-287" />
                    <stop offset="50%" stopColor="#8b5cf6" data-id="element-288" />
                    <stop offset="100%" stopColor="#7c3aed" data-id="element-289" />
                  </linearGradient>
                  <filter id="pawnShadow" data-id="element-290">
                    <feDropShadow dx="0" dy="8" stdDeviation="4" floodOpacity="0.4" data-id="element-291" />
                  </filter>
                </defs>
                {/* Pawn base */}
                <ellipse cx="40" cy="110" rx="30" ry="8" fill="url(#pawnGrad)" filter="url(#pawnShadow)" data-id="element-292" />
                {/* Pawn body */}
                <path d="M 25 110 Q 25 80 30 60 L 50 60 Q 55 80 55 110 Z" fill="url(#pawnGrad)" filter="url(#pawnShadow)" data-id="element-293" />
                {/* Pawn neck */}
                <rect x="35" y="45" width="10" height="15" rx="2" fill="url(#pawnGrad)" data-id="element-294" />
                {/* Pawn head */}
                <circle cx="40" cy="35" r="15" fill="url(#pawnGrad)" filter="url(#pawnShadow)" data-id="element-295" />
                {/* Highlight */}
                <ellipse cx="35" cy="30" rx="6" ry="8" fill="white" opacity="0.4" data-id="element-296" />
              </svg>
            </motion.div>

            {/* Player avatars on their lanes */}
            <div className="absolute inset-0 pointer-events-none" data-id="element-297">
              {[0, 1, 2, 3].map(pathIdx => {
              const pathPlayers = playersByPath[pathIdx];
              const pathColor = PATH_COLORS[pathIdx];
              // Position avatars at the bottom of their respective lane
              const lanePositions = ['12%', '37%', '62%', '87%'];
              return <div key={pathIdx} className="absolute flex gap-1" style={{
                left: lanePositions[pathIdx],
                bottom: '-5%',
                transform: 'translateX(-50%) rotateX(-60deg)'
              }} data-id="element-298">
                    {pathPlayers.map((p, idx) => <motion.div key={p.id} initial={{
                  y: -20,
                  opacity: 0
                }} animate={{
                  y: [0, -6, 0],
                  opacity: 1
                }} transition={{
                  y: {
                    duration: 1.8,
                    repeat: Infinity,
                    delay: idx * 0.2
                  },
                  opacity: {
                    duration: 0.4,
                    delay: idx * 0.1
                  }
                }} className="flex flex-col items-center" data-id="element-299">
                        <div className="rounded-full border-[3px] border-white shadow-2xl overflow-hidden flex items-center justify-center" style={{
                    width: 48,
                    height: 48,
                    background: pathColor.fill,
                    boxShadow: `0 0 20px ${pathColor.stroke}`
                  }} data-id="element-300">
                          {p.avatar ? <img src={p.avatar} alt="" className="w-full h-full object-cover" data-id="element-301" /> : <span className="text-white font-bold text-xl" data-id="element-302">
                              {p.name.charAt(0).toUpperCase()}
                            </span>}
                        </div>
                        <div className="mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white whitespace-nowrap" style={{
                    background: pathColor.stroke
                  }} data-id="element-303">
                          {p.name}
                        </div>
                      </motion.div>)}
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>

      {/* Left side sign - Choose Your Path */}
      <div className="absolute left-4 bottom-[25%] bg-gray-900/90 backdrop-blur-sm rounded-xl p-4 shadow-2xl border-2 border-pink-400 max-w-[140px]" data-id="element-304">
        <h3 className="text-pink-400 font-bold text-sm mb-3 text-center" data-id="element-305">
          CHOOSE
          <br data-id="element-306" />
          YOUR PATH
        </h3>
        <div className="space-y-2" data-id="element-307">
          {PATH_COLORS.map((path, i) => <div key={i} className="flex items-center gap-2 text-white text-xs font-bold" data-id="element-308">
              <span data-id="element-309">{path.emoji}</span>
              <span style={{
            color: path.fill
          }} data-id="element-310">
                {path.name.toUpperCase()}
              </span>
            </div>)}
        </div>
      </div>

      {/* Right side sign - Today's Choices */}
      <div className="absolute right-4 bottom-[25%] bg-gray-900/90 backdrop-blur-sm rounded-xl p-4 shadow-2xl border-2 border-purple-400 max-w-[140px]" data-id="element-311">
        <p className="text-purple-300 font-bold text-sm text-center leading-tight" data-id="element-312">
          TODAY'S
          <br data-id="element-313" />
          CHOICES
          <br data-id="element-314" />
          <span className="text-pink-300" data-id="element-315">TOMORROW'S</span>
          <br data-id="element-316" />
          <span className="text-pink-300" data-id="element-317">LIFE</span>
        </p>
        <div className="mt-2 text-center text-2xl" data-id="element-318">😊</div>
      </div>

      {/* Flower planters bottom edges */}
      <div className="absolute bottom-0 left-[5%] w-16 h-16 bg-pink-300 rounded-t-full flex items-end justify-center pb-2" data-id="element-319">
        <div className="text-2xl" data-id="element-320">🌸</div>
      </div>
      <div className="absolute bottom-0 right-[5%] w-16 h-16 bg-pink-300 rounded-t-full flex items-end justify-center pb-2" data-id="element-321">
        <div className="text-2xl" data-id="element-322">🌺</div>
      </div>

      {/* Top title - billboard style */}
      <div className="absolute top-0 left-0 right-0 flex flex-col items-center pt-6 pb-4 bg-gradient-to-b from-black/40 via-black/10 to-transparent pointer-events-none z-10" data-id="element-323">
        <motion.div className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 px-8 py-3 rounded-full shadow-2xl border-4 border-white/30" animate={{
        boxShadow: ['0 0 20px rgba(236,72,153,0.6)', '0 0 40px rgba(168,85,247,0.6)', '0 0 20px rgba(236,72,153,0.6)']
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} data-id="element-324">
          <h1 className="text-3xl md:text-5xl font-bold text-white drop-shadow-lg text-center" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-325">
            Your Journey Begins!
          </h1>
        </motion.div>
        <p className="text-xs md:text-sm text-white font-bold tracking-widest uppercase bg-black/40 px-6 py-1.5 rounded-full backdrop-blur-sm text-center mt-2" data-id="element-326">
          Follow your path. Make your choices. Build your life.
        </p>
      </div>

      {/* Roll Dice CTA */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center z-20" data-id="element-327">
        <motion.button onClick={onStartPlaying} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.97
      }} className="group flex items-center gap-4 bg-purple-900/90 backdrop-blur-md p-2 pr-8 rounded-full border-4 border-pink-400 shadow-[0_0_40px_rgba(236,72,153,0.8)] hover:bg-purple-800 transition-colors" data-id="element-328">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-inner" data-id="element-329">
            <motion.span className="text-4xl" animate={{
            rotate: [0, 360]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-330">
              🎲
            </motion.span>
          </div>
          <div className="flex flex-col items-start" data-id="element-331">
            <span className="text-white font-bold text-lg uppercase tracking-wider" data-id="element-332">
              Roll the dice
            </span>
            <span className="text-pink-200 text-sm" data-id="element-333">
              to start your journey!
            </span>
          </div>
        </motion.button>
      </div>

      {/* Twinkling billboard lights */}
      {[...Array(20)].map((_, i) => <motion.div key={i} className="absolute w-1 h-1 bg-yellow-300 rounded-full" style={{
      top: `${20 + Math.random() * 60}%`,
      left: `${Math.random() * 100}%`
    }} animate={{
      opacity: [0, 1, 0],
      scale: [0.8, 1.2, 0.8]
    }} transition={{
      duration: 1 + Math.random() * 2,
      repeat: Infinity,
      delay: Math.random() * 2
    }} data-id="element-334" />)}
    </div>;
}